import { Suspense, lazy, useLayoutEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes as ReactRoutes,
  useInRouterContext,
  Router,
} from "react-router-dom";

import TaxSummaryContainer from "./containers/TaxSummary/TaxSummaryContainer";
import Sorry from "./containers/User/components/Sorry";
import AuthLayout from './containers/Auth/components/AuthLayout';
import Layout from "./components/Layout";
import { history } from "./store";
import PersonalInfoForm from "./containers/User/components/PersonalInfoForm";
import ReviewForm from "./containers/User/components/ReviewForm";
import AdditionalTaxInfo from "./containers/User/components/AdditionalTaxInfo";
import TermsAndConditions from "./components/TermsAndConditions/TermsAndConditions";
import PrivacyPolicy from "./components/PrivacyPolicy/PrivacyPolicy";
import Welcome from "./containers/User/components/Welcome";
import BalanceReview from "./containers/FinalBalance/components/BalanceReview";
import BalanceOptions from "./containers/FinalBalance/components/BalanceOptions";
import Signature from "./containers/FinalBalance/components/Signature";
import PaymentInformation from "./containers/Payment/components/PaymentInformation";
import RefundInformation from "./containers/Payment/components/RefundInformation";
import FinalConfirmation from "./components/FinalConfirmation";
import TransitionContainer from "./containers/Transition/TransitionContainer";
import PostSignatureTransition from "./containers/Transition/components/PostSignatureTransition";
import TaxReturnStateTransitionSorry from "./containers/Transition/components/TaxReturnStateTransitionSorry";
import FilingClosed from "./containers/User/components/FilingClosed";
import AcceptTerms from "./containers/Auth/Register/components/AcceptTerms";
import AgeVerification from "./containers/Auth/Register/components/AgeVerification";
import AddressVerification from "./containers/Auth/Register/components/AddressVerification";
import PhoneNumberVerification from "./containers/Auth/Register/components/PhoneNumberVerification";
import EnterName from "./containers/Auth/Register/components/EnterName";
import EnterEmail from "./containers/Auth/Register/components/EnterEmail";
import SsnVerification from "./containers/Auth/Register/components/SsnVerification";
import SignUpForm from "./containers/Auth/Register/components/SignUpForm";
import { History } from "history";
import AddressVerificationFormGroup from "./containers/User/components/AddressVerificationFormGroup";

const Login = lazy(() => import("./containers/Auth/Login/LoginContainer"));
const Register = lazy(
  () => import("./containers/Auth/Register/RegisterContainer")
);
const ForgotPassword = lazy(
  () => import("./containers/Auth/ForgotPassword/ForgotPasswordContainer")
);
const ResetPassword = lazy(
  () => import("./containers/Auth/ResetPassword/ResetPasswordContainer")
);
const User = lazy(() => import("./containers/User/UserContainer"));
const Upload = lazy(() => import("./containers/Upload/UploadContainer"));
const Payment = lazy(() => import("./containers/Payment/PaymentContainer"));
const FinalBalance = lazy(
  () => import("./containers/FinalBalance/FinalBalanceContainer")
);
const Settings = lazy(() => import("./containers/Settings/SettingsContainer"));
const Policies = lazy(() => import("./containers/Policies/PoliciesContainer"));
const Help = lazy(() => import("./containers/Help/HelpContainer"));

// redux-first-history doesn't support React Router v7 directly, so we use a custom HistoryRouter component
const HistoryRouter = ({
  basename,
  children,
  history,
}: {
  basename?: string;
  children?: React.ReactNode;
  history: History;
}) => {
  const [histState, setHistState] = useState({
    action: history.action,
    location: history.location,
  });

  useLayoutEffect(() => {
    const unlisten = history.listen((update) => {
      setHistState(update);
    });

    return () => {
      unlisten();
    };
  }, [history]);
  
  return (
    <Router 
      basename={basename} 
      location={histState.location} 
      navigator={history} 
      navigationType={histState.action}>
      {children}
    </Router>
  );
};

const Routes = ({ isLoggedIn }: any) => {

  return (
    <HistoryRouter history={history}>
      <Suspense fallback={<div>Loading...</div>}>
        <AppRoutes isLoggedIn={isLoggedIn}></AppRoutes>
      </Suspense>
    </HistoryRouter>
  );
};

const AppRoutes = ({ isLoggedIn }: any) => {
  function RequireAuth({ children }: any) {
    return isLoggedIn ? children : <Navigate to="/" />;
  }
  useInRouterContext
  return (
    <ReactRoutes>
          <Route
            path="/"
            element={isLoggedIn ? <Navigate to="/user" replace /> : <Login />}
          />
          <Route path="/register" element={<Navigate to="/register/terms" replace />} />
          <Route path="/register/sorry" element={<AuthLayout><Sorry/></AuthLayout>} />
          <Route path="/register" element={<Register />}>
            <Route path="/register/terms" element={<AcceptTerms />} />
            <Route path="/register/age" element={<AgeVerification />} />
            <Route path="/register/address" element={<AddressVerification />} />
            <Route path="/register/phone" element={<PhoneNumberVerification />} />
            <Route path="/register/name" element={<EnterName />} />
            <Route path="/register/email" element={<EnterEmail />} />
            <Route path="/register/ssn" element={<SsnVerification />} />
            <Route path="/register/signup" element={<SignUpForm />} />
          </Route>

          <Route path="/forgot_password" element={<ForgotPassword />} />
          <Route path="/reset_password" element={<ResetPassword />} />
          <Route path="/user" element={
                      <RequireAuth>
                        <Navigate to={"/user/welcome-back"} />
                      </RequireAuth>
                    } />
          <Route path="/user" element={<RequireAuth><User/></RequireAuth>}>
            {/*
              TODO copy existing Welcome component and tweak it to accommodate other user flows (make payment, etc)
              <Route path="/user/welcome-back" element={<WelcomeBack />} /> */}
            {/* TODO coupling this to the User container may require a separate route for the readonly T&C */}
            <Route path="/user/terms-and-conditions" element={<RequireAuth><User/></RequireAuth>} />
            <Route path="/user/welcome-back" element={<RequireAuth><Welcome/></RequireAuth>} />
            <Route path="/user/update-address" element={<RequireAuth><AddressVerificationFormGroup /></RequireAuth>} />
            <Route path="/user/personal-info" element={<PersonalInfoForm />} />
            <Route path="/user/spouse-info" element={<PersonalInfoForm />} />
            <Route path="/user/additional-info" element={<AdditionalTaxInfo />} />
            <Route path="/user/review" element={<ReviewForm />} />
            <Route path="/user/sorry" element={<Sorry />} />
            <Route path="/user/filing-closed" element={<FilingClosed />} />
            {/* TODO this checkpoint URL isn't advertised, as refactoring to userSetup.stepValidity must take place
            <Route path="/user/checkpoint" element={
                      <RequireAuth>
                        <UserCheckpointRedirector checkpoints={ userCheckpoints }/>
                      </RequireAuth>
                    } />
              */}
          </Route>
          <Route path="/upload/sorry" element={<RequireAuth><Layout><Sorry /></Layout></RequireAuth>} />
          <Route path="/upload/:type" element={<RequireAuth><Upload /></RequireAuth>} />

          <Route path="/balance" element={<FinalBalance />}>
            <Route path="/balance" element={<BalanceReview />} />
            <Route path="/balance/review" element={<BalanceOptions />} />
            <Route path="/balance/sign" element={<Signature />} />
            <Route path="/balance/sorry" element={<Sorry />} />
          </Route>

          <Route path="/transition" element={<TransitionContainer/>}>
            <Route path="/transition/post-signature" element={<PostSignatureTransition />} />
            {/* User would get sent here if backend state doesn't change to a payment or completed state */}
            <Route path="/transition/post-signature-error" element={<TaxReturnStateTransitionSorry actionCompletedButMessage="We've reviewed your balance"/>} />
          </Route>

          <Route path="/payment" element={<Payment />}>
            <Route path="/payment/balance" element={<PaymentInformation />} />
            <Route path="/payment/refund" element={<RefundInformation />} />
            <Route path="/payment/complete" element={<FinalConfirmation />} />
            <Route path="/payment/sorry" element={<TaxReturnStateTransitionSorry actionCompletedButMessage="Your payment was successful"/>} />
          </Route>

          <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />

          <Route path="/policies" element={<RequireAuth><Policies /></RequireAuth>} />
          <Route path="/policies/terms" element={<RequireAuth><Layout><TermsAndConditions termsSigned={true}/></Layout></RequireAuth>}/>
          <Route path="/policies/privacy" element={<RequireAuth><Layout><PrivacyPolicy /></Layout></RequireAuth>}/>
          <Route path="/help" element={<RequireAuth><Help /></RequireAuth>} />
          <Route
            path="/tax-summary"
            element={<RequireAuth ><TaxSummaryContainer /></RequireAuth>}
          />
          <Route path="*" element={<p>Page not found</p>} />
        </ReactRoutes>
  );
}

export default Routes;
